import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Pencil, Trash2, Plus, X } from "lucide-react";

const blank = () => ({ name: "", phone: "", address: "", gst_number: "" });

export default function Customers() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");

  const load = () => api.get("/customers").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this customer?")) return;
    await api.delete(`/customers/${id}`); load();
  };

  return (
    <div data-testid="customers-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Customers</h1>
          <p className="page-sub">{items.length} customer(s)</p>
        </div>
        <button className="btn btn-primary" data-testid="add-customer-btn" onClick={() => { setErr(""); setEditing(blank()); }}>
          <Plus size={16}/> Add Customer
        </button>
      </div>

      <div className="card-flush" data-testid="customers-table">
        <table className="tbl">
          <thead><tr><th>Name</th><th>Phone</th><th>Address</th><th>GST</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={5} style={{ textAlign: "center", color: "var(--text-muted)", padding: 32 }}>No customers yet.</td></tr>}
            {items.map((c) => (
              <tr key={c.id} data-testid={`customer-row-${c.id}`}>
                <td><strong>{c.name}</strong></td>
                <td className="font-mono">{c.phone || "—"}</td>
                <td style={{ color: "var(--text-muted)" }}>{c.address || "—"}</td>
                <td className="font-mono">{c.gst_number || "—"}</td>
                <td>
                  <button className="btn btn-ghost btn-sm" data-testid={`edit-customer-${c.id}`} onClick={() => { setErr(""); setEditing(c); }}><Pencil size={14}/></button>
                  <button className="btn btn-ghost btn-sm" data-testid={`delete-customer-${c.id}`} onClick={() => remove(c.id)}><Trash2 size={14}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <CustomerForm value={editing} err={err} setErr={setErr} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/>}
    </div>
  );
}

function CustomerForm({ value, err, setErr, onClose, onSaved }) {
  const [c, setC] = useState(value);
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setC({ ...c, [k]: v });
  const save = async () => {
    setBusy(true); setErr("");
    try {
      if (c.id) await api.put(`/customers/${c.id}`, c);
      else await api.post("/customers", c);
      onSaved();
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="customer-modal" style={{ maxWidth: 520 }}>
        <div className="modal-h"><h2>{c.id ? "Edit Customer" : "Add Customer"}</h2><button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16}/></button></div>
        <div className="modal-b">
          {err && <div className="error-msg" style={{ marginBottom: 12 }}>{err}</div>}
          <div className="field" style={{ marginBottom: 12 }}><label>Name *</label><input data-testid="cust-name" className="input" value={c.name} onChange={(e) => upd("name", e.target.value)}/></div>
          <div className="field" style={{ marginBottom: 12 }}><label>Phone</label><input data-testid="cust-phone" className="input" value={c.phone} onChange={(e) => upd("phone", e.target.value)}/></div>
          <div className="field" style={{ marginBottom: 12 }}><label>Address</label><textarea data-testid="cust-addr" className="input" rows="3" value={c.address} onChange={(e) => upd("address", e.target.value)}/></div>
          <div className="field"><label>GST Number (Optional)</label><input data-testid="cust-gst" className="input" value={c.gst_number || ""} onChange={(e) => upd("gst_number", e.target.value)}/></div>
        </div>
        <div className="modal-f">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" data-testid="save-customer-btn" disabled={busy || !c.name} onClick={save}>{busy ? "Saving..." : "Save"}</button>
        </div>
      </div>
    </div>
  );
}
